import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, BarChart3, BrainCircuit, CheckCircle2, Clock3, Scale,
  Pencil, ShieldCheck, Sparkles, Target, Trash2, UserCheck,
} from 'lucide-react';
import { api } from '../api.js';
import {
  Avatar, Badge, Button, EmptyState, IconButton, Modal, PageHeader, ScoreRing, SectionHeader,
} from '../components/ui.jsx';
import { formatDate, getCandidateConfidence, getCandidateScore } from '../utils.js';

function EditCalibrationModal({ item, open, onClose, onSaved, notify }) {
  const [score, setScore] = useState('');
  const [decision, setDecision] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open || !item) return;
    setScore(item.adjustedScore ?? '');
    setDecision(item.decision || '');
    setNotes(item.notes || '');
  }, [item, open]);

  const save = async () => {
    if (!decision.trim()) return notify('请填写人工结论', 'danger');
    setSaving(true);
    try {
      await api.updateCalibration(item.candidateId, item.id, {
        adjustedScore: score, decision, notes,
      });
      notify('校准评价已更新');
      await onSaved();
      onClose();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`编辑校准 · ${item?.displayName || ''}`}
      description="只有创建者本人可以修改这条评价。"
      footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" loading={saving} onClick={save}>保存修改</Button></>}
    >
      <div className="calibration-form">
        <label><span>人工调整分</span><div className="score-input"><input type="number" min="0" max="100" value={score} onChange={(event) => setScore(event.target.value)} /><strong>/ 100</strong></div></label>
        <label><span>人工结论</span><input value={decision} onChange={(event) => setDecision(event.target.value)} /></label>
        <label><span>复核备注</span><textarea rows="5" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
      </div>
    </Modal>
  );
}

export default function CalibrationPage({ state, navigate, refresh, notify }) {
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const calibration = state?.calibration || {};
  const reviewCandidates = useMemo(() => (state?.candidates || []).filter((candidate) => {
    if (candidate.status !== 'needs_review') return false;
    if (filter === 'low-confidence') return getCandidateConfidence(candidate) < 0.68;
    if (filter === 'risk') return candidate.risks?.some((risk) => risk.severity === 'high');
    if (filter === 'potential') return candidate.potential?.score >= 88;
    return true;
  }), [state?.candidates, filter]);
  const sampleTarget = 30;
  const progress = Math.min(100, (calibration.count || 0) / sampleTarget * 100);
  const removeCalibration = async (item) => {
    if (!window.confirm(`确定删除 ${item.displayName} 的这条校准评价吗？`)) return;
    setDeletingId(item.id);
    try {
      await api.deleteCalibration(item.candidateId, item.id);
      notify('校准评价已删除');
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setDeletingId(''); }
  };

  return (
    <div className="page calibration-page">
      <PageHeader eyebrow="人机协作" title="人工校准" description="记录分歧与最终判断，让评分体系从真实招聘结果中改进" actions={<Button icon={ShieldCheck} onClick={() => navigate('audit')}>查看审计记录</Button>} />

      <section className="calibration-metrics">
        <article><span><UserCheck size={18} /></span><div><small>已校准样本</small><strong>{calibration.count || 0}</strong></div><i style={{ '--value': `${progress}%` }} /></article>
        <article><span><Scale size={18} /></span><div><small>平均绝对调整</small><strong>{calibration.averageAbsoluteAdjustment || 0}<em>分</em></strong></div></article>
        <article><span><Target size={18} /></span><div><small>±5 分一致率</small><strong>{calibration.count ? calibration.withinFivePoints : '—'}{calibration.count ? <em>%</em> : null}</strong></div></article>
        <article><span><BarChart3 size={18} /></span><div><small>排序评估</small><strong>{calibration.count >= sampleTarget ? '可计算' : '待样本'}</strong></div><Badge tone={calibration.count >= sampleTarget ? 'success' : 'warning'}>NDCG</Badge></article>
      </section>

      <div className="calibration-layout">
        <section className="panel calibration-queue">
          <SectionHeader title="复核队列" description="优先处理低置信度、强风险与高潜力边界样本" action={<Badge tone="warning">{reviewCandidates.length} 人</Badge>} />
          <div className="calibration-filters">{[['all', '全部'], ['low-confidence', '低置信度'], ['risk', '强风险'], ['potential', '高潜力']].map(([id, label]) => <button key={id} className={filter === id ? 'active' : ''} onClick={() => setFilter(id)}>{label}</button>)}</div>
          {reviewCandidates.length === 0 ? <EmptyState icon={CheckCircle2} title="当前筛选项没有待复核候选人" /> : (
            <div className="calibration-candidate-list">{reviewCandidates.map((candidate, index) => (
              <button key={candidate.id} onClick={() => navigate('candidate', candidate.id)}>
                <Avatar name={candidate.displayName} tone={index} /><div><strong>{candidate.displayName}</strong><span>{candidate.risks?.[0]?.title || '人工判断待完成'}</span><small>{candidate.evidenceCount} 条证据 · 置信度 {Math.round(getCandidateConfidence(candidate) * 100)}%</small></div><ScoreRing score={getCandidateScore(candidate)} size={48} /><span className="calibrate-action"><Scale size={15} />复核</span><ArrowRight size={15} />
              </button>
            ))}</div>
          )}
        </section>

        <aside className="calibration-aside">
          <section className="calibration-goal">
            <span className="side-panel-icon"><Sparkles size={20} /></span><h3>首轮校准目标</h3><div className="goal-progress"><div><i style={{ width: `${progress}%` }} /></div><strong>{calibration.count || 0} / {sampleTarget}</strong></div><p>达到 30 个同岗位人工样本后，再计算 NDCG、Top 10 命中率与分维度偏差。</p>
          </section>
          <section className="calibration-principles"><h3>记录重点</h3><ul><li><BrainCircuit size={15} /><span><strong>为什么不同意</strong>记录证据与判断，不只改分</span></li><li><AlertTriangle size={15} /><span><strong>误判类型</strong>区分解析、上下文、权重和模型问题</span></li><li><Clock3 size={15} /><span><strong>最终结果</strong>后续补充面试与试用期反馈</span></li></ul></section>
        </aside>
      </div>

      <section className="panel calibration-history">
        <SectionHeader title="最近校准" description="人工调整不会覆盖原始 AI 版本" />
        {(calibration.recent || []).length === 0 ? <EmptyState icon={Scale} title="尚未产生校准记录" description="从上方队列打开候选人并提交人工结论。" /> : (
          <table><thead><tr><th>候选人</th><th>复核人</th><th>AI 分</th><th>人工分</th><th>最终动作</th><th>时间</th><th>操作</th></tr></thead><tbody>{calibration.recent.map((item) => {
            const owned = item.reviewerId === state?.currentUser?.id;
            return <tr key={item.id}><td>{item.displayName}</td><td>{item.reviewer}</td><td>{item.previousScore ?? '—'}</td><td><strong>{item.adjustedScore ?? '—'}</strong></td><td><Badge tone="accent">{item.decision}</Badge></td><td>{formatDate(item.createdAt, true)}</td><td><div className="calibration-row-actions">{owned ? <><IconButton icon={Pencil} label="编辑校准" onClick={() => setEditing(item)} /><IconButton icon={Trash2} label="删除校准" variant="danger-ghost" disabled={deletingId === item.id} onClick={() => removeCalibration(item)} /></> : <span>—</span>}</div></td></tr>;
          })}</tbody></table>
        )}
      </section>
      <EditCalibrationModal item={editing} open={Boolean(editing)} onClose={() => setEditing(null)} onSaved={refresh} notify={notify} />
    </div>
  );
}
